import { memo, RefObject, useContext } from 'react';
import {
  VirtuosoMessageListProps,
  VirtuosoMessageListLicense,
  VirtuosoMessageList,
  VirtuosoMessageListMethods,
} from '@virtuoso.dev/message-list';
import { CompareModeType, ConversationRole, Message } from 'src/types';
import { UserMessage } from 'src/v2/components/UserMessage';
import { ThreadMessage } from 'src/v2/components/ThreadMessage';
import styles from './MessageListOptimized.module.scss';
import classNames from 'classnames';
import { MESSAGE_KEY } from 'src/constants';
import {
  useComparingConversationsParams,
  useCurrentConversationMessageList,
} from 'src/hooks';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import { useChatHistory } from 'src/hooks';

const InnerItem = memo(
  ({ data, isLast }: { data: Message; isLast: boolean }) => {
    const userMessage = data.role === ConversationRole.USER;

    return (
      <div>
        {userMessage ? (
          <UserMessage message={data} />
        ) : (
          <ThreadMessage message={data} isLast={isLast} />
        )}
      </div>
    );
  },
);

const ItemContent: VirtuosoMessageListProps<
  Message,
  { messageListLength: number }
>['ItemContent'] = ({
  data,
  index,
  context: { messageListLength },
}: {
  data: Message;
  index: number;
  context: { messageListLength: number };
}) => {
  return <InnerItem data={data} isLast={index === messageListLength - 1} />;
};

interface MessageListOptimizedProps {
  conversationId?: string;
  messageListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareType?: CompareModeType;
}

export const MessageListOptimized = ({
  conversationId,
  messageListRef,
  compareType,
}: MessageListOptimizedProps) => {
  const { isNinjaRetired } = useChatHistory();
  const { messageListLength } =
    useCurrentConversationMessageList(conversationId);
  const { isCompareMode } = useComparingConversationsParams();

  const { isInputBoxExpanded } = useContext(ThreadInputBoxContext);

  return (
    <div
      className={classNames(styles.root, {
        [styles.morePaddings]: isInputBoxExpanded,
        [styles.withoutBottomPadding]: isCompareMode,
        [styles.ninjaRetired]: isNinjaRetired,
      })}
    >
      <VirtuosoMessageListLicense licenseKey={MESSAGE_KEY}>
        <VirtuosoMessageList<Message, { messageListLength: number }>
          key={compareType || 'first'}
          initialData={[]}
          ref={messageListRef}
          computeItemKey={({ data }: { data: Message }) =>
            data.message_id || ''
          }
          initialLocation={{ index: 'LAST', align: 'end' }}
          ItemContent={ItemContent}
          style={{ flex: 1 }}
          context={{
            messageListLength,
          }}
        />
      </VirtuosoMessageListLicense>
    </div>
  );
};
